import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Alert, Button, Card, Modal, Space, Spin, Tag, Typography } from 'antd';
import { HolderOutlined, StarFilled } from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { worlds_reorder } from '../../../redux/actions/worldsActions';

// Utils
import { object_ref_id } from '../../../utils/world_medals';
import { star_goal_flags, recipes_count_by_world } from '../../../utils/star_goals';

const FLAG_COLORS = { ok: '#52c41a', warn: '#faad14', error: '#ff4d4f' };

const SortableWorld = (props) => {
	const { id, children } = props;

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		cursor: 'grab',
		marginBottom: '0.5rem'
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{children}
		</div>
	);
};

SortableWorld.propTypes = {
	id: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired
};

const WorldsOrder = (props) => {
	const { messageApi, isModalOpen, setIsModalOpen } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_worlds_loading, all_worlds, worlds_reorder_loading } = useSelector(state => state.worlds);

	const { all_recipes } = useSelector(state => state.recipes);

	const { all_packages } = useSelector(state => state.packages);

	const [localWorlds, setLocalWorlds] = useState([]);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor)
	);

	useEffect(() => {
		// Order by the persisted map order so the list reflects the current map
		const sorted = [...(all_worlds.worlds || [])].sort(
			(a, b) => (a.order || 0) - (b.order || 0)
		);
		setLocalWorlds(sorted);
	}, [all_worlds.worlds]);

	const recipeCounts = recipes_count_by_world(all_recipes?.recipes);

	// Live validation against the CURRENT drag order (not the persisted one)
	const flags = star_goal_flags(
		localWorlds, all_recipes?.recipes, all_packages?.packages
	);

	const invalidCount = localWorlds.filter((world) => {
		const flag = flags[object_ref_id(world._id)];
		return flag !== 'ok' && flag !== 'premium';
	}).length;

	const handleCancel = () => {
		setIsModalOpen(false);
	}

	const handleDragEnd = (event) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = localWorlds.findIndex(world => object_ref_id(world._id) === active.id);
			const newIndex = localWorlds.findIndex(world => object_ref_id(world._id) === over.id);
			setLocalWorlds(arrayMove(localWorlds, oldIndex, newIndex));
		}
	};

	const onOk = () => {
		const worldsBody = {
			worlds: localWorlds.map((world, index) => ({
				world: object_ref_id(world._id),
				order: index
			}))
		};

		dispatch(worlds_reorder(messageApi, worldsBody, handleCancel));
	}

	return (
		<Modal
			title={t('worlds.reorder.modalTitle')}
			centered
			open={isModalOpen}
			onCancel={handleCancel}
			maskClosable={false}
			width={600}
			footer={[
				<Button key='cancel' onClick={handleCancel}>
					{t('buttons.cancel')}
				</Button>,
				<Button
					key='submit'
					type='primary'
					loading={worlds_reorder_loading}
					onClick={onOk}
				>
					{t('buttons.save')}
				</Button>,
			]}
		>
			<Typography.Text type='secondary'>
				{t('worlds.reorder.hint')}
			</Typography.Text>
			<Spin spinning={all_worlds_loading}>
				<div style={{ marginTop: '1rem', maxHeight: '50vh', overflowY: 'auto' }}>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={localWorlds.map(world => object_ref_id(world._id))}
							strategy={verticalListSortingStrategy}
						>
							{localWorlds.map((world, index) => {
								const id = object_ref_id(world._id);
								const flag = flags[id] ?? 'ok';

								return (
									<SortableWorld key={id} id={id}>
										<Card size='small'>
											<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
												<HolderOutlined />
												<Typography.Text strong>{index + 1}.</Typography.Text>
												<Typography.Text style={{ flex: 1 }}>{world.name}</Typography.Text>
												{flag === 'premium'
													?	<Tag color='gold' style={{ marginRight: 0 }}>
															{t('worlds.details.premiumTag')}
														</Tag>
													:	<>
															<Space size={4}>
																<StarFilled style={{ color: '#E89A00', fontSize: 12 }} />
																<Typography.Text>{world.star_goal ?? 0}</Typography.Text>
															</Space>
															<Typography.Text type='secondary' style={{ fontSize: 12 }}>
																({recipeCounts[id] ?? 0})
															</Typography.Text>
															<span style={{ width: 10, height: 10, borderRadius: '50%', background: FLAG_COLORS[flag], flex: 'none' }} />
														</>}
											</div>
										</Card>
									</SortableWorld>
								);
							})}
						</SortableContext>
					</DndContext>
				</div>
			</Spin>
			{invalidCount > 0 && (
				<Alert
					type='warning'
					showIcon
					style={{ marginTop: 12 }}
					message={t('worlds.reorder.invalidCount', { count: invalidCount })}
				/>
			)}
		</Modal>
	)
}

WorldsOrder.propTypes = {
	messageApi: PropTypes.object.isRequired,
	isModalOpen: PropTypes.bool.isRequired,
	setIsModalOpen: PropTypes.func.isRequired
}

export default WorldsOrder;
